// Pure classification helpers for a character's loose OGS bundle. Split out from
// ogsContentPacks.js (which couples fetch + the Vite import.meta.env AAM base) so
// it stays unit-testable under plain `node --test` — same split as bakeKind.js.

import { fp16NameFor } from '../ogs/assets/sceneManifest.ts'

// The decoder stages, by filename STEM. A single-region bundle names them
// `geom.onnx`; a multi_region one suffixes the branch (`geom_head.onnx`). Both
// prefer the quantized `_fp16` variant and fall back to the fp32 file.
export const DECODER_STEMS = ['geom', 'appr', 'trunk']

// The decoder stages that ship an fp16 variant AND have a lazy fp32 fallback.
// Trunk is deliberately absent: no fp16 trunk asset is exported, and OGSBranch
// takes the trunk bytes eagerly (no fetchBytes path), so it has no fallback.
export const FP16_CAPABLE_STEMS = ['geom', 'appr']

// Non-decoder files the lift pipeline needs before it can render, by STEM.
// `mesh` covers mesh.json/mesh.bin and their per-branch mesh_head.* form.
// export_manifest.json is also the marker applyCharacterPack probes to detect an
// OGS character, so a bundle without it never renders at all.
export const OGS_REQUIRED_STEMS = ['mesh', 'rig_names', 'export_manifest', 'scene']

// Present-when-used, never required. NOT rig2mesh: a declared rig2mesh is never
// fetched any more (sceneManifest.multiRegionFileList refuses to list one), so
// labelling it 'optional' tells the content browser a ~74 MB dead file is useful.
// `orl_pack` IS here — it is the character's face rig, and its absence is what
// leaves a head frozen at neutral.
// vertex_association pairs the driver GLBs with the texavatars vertex order;
// vis_mask / rig_params / rig_presets / frame_vertices / deploy_info are
// opportunistic extras the pipeline reads only if the bundle ships them.
// Mirrors the `optionalNames` set ogsContentPacks actually fetches.
export const OGS_OPTIONAL_STEMS = [
  'orl_pack', 'vis_mask', 'rig_params', 'rig_presets', 'frame_vertices', 'deploy_info',
  'vertex_association', 'hair',
]

// The rig-optional boot probe (applyCharacterPack): a character whose /ogs
// collection carries export_manifest.json is an OGS splat character — no rig GLB.
export function hasOgsBundleMarker(rows) {
  if (!Array.isArray(rows)) return false
  return rows.some((r) => (r?.filename ?? r?.name) === 'export_manifest.json')
}

/** Strip the extension and any `_fp16` / per-branch suffix, so single-region and
 *  multi_region spellings of the same role collapse to one stem:
 *    geom.onnx · geom_fp16.onnx · geom_head.onnx · geom_head_fp16.onnx → 'geom'
 *    mesh.bin  · mesh_body.json                                       → 'mesh'
 *  Returns { stem, fp16 }. */
function splitBundleName(filename) {
  if (typeof filename !== 'string' || !filename) return { stem: '', fp16: false }
  const dot = filename.lastIndexOf('.')
  let base = dot > 0 ? filename.slice(0, dot) : filename
  const fp16 = base.endsWith('_fp16')
  if (fp16) base = base.slice(0, -'_fp16'.length)
  // Longest known stem that the name starts with, so 'rig_names' isn't shadowed
  // by a shorter prefix and 'geom_combined' still resolves to 'geom'.
  const known = [...OGS_REQUIRED_STEMS, ...OGS_OPTIONAL_STEMS, ...DECODER_STEMS]
    .sort((a, b) => b.length - a.length)
  const stem = known.find((s) => base === s || base.startsWith(`${s}_`)) ?? base
  return { stem, fp16, base }
}

// Inspector hint: how the render pipeline treats this bundle filename. Works for
// BOTH layouts — a multi_region bundle's per-branch names (geom_head.onnx,
// mesh_body.bin, trunk_head.onnx) used to fall through to 'extra', which meant
// the content browser labelled essentially every file of every real bundle wrong.
export function ogsFileRole(filename) {
  const { stem, fp16, base } = splitBundleName(filename)
  if (!stem) return 'extra'
  // A GLB in an OGS bundle is a DRIVER asset — the per-part skeleton rig plus a body
  // clip whose skinned verts drive the gaussians (the part-drive path). Keyed on the
  // extension rather than a filename so this doesn't hardcode a clip name.
  if (typeof filename === 'string' && filename.toLowerCase().endsWith('.glb')) return 'optional'
  // `*_combined.onnx` are the donor's merged head+body sessions (its batching
  // probe). They ship in real bundles but this repo's loader never requests
  // them — the scene manifest names the per-branch files — so they are extras,
  // not required decoders.
  if (base.includes('_combined')) return 'extra'
  // A quantized decoder is preferred but never required — the fp32 file is the
  // fallback, so the bundle is complete without the fp16 one.
  if (fp16) return 'optional'
  if (OGS_OPTIONAL_STEMS.includes(stem)) return 'optional'
  if (DECODER_STEMS.includes(stem)) return 'required'
  if (OGS_REQUIRED_STEMS.includes(stem)) return 'required'
  return 'extra'
}

// Is a REQUIRED bundle file resolvable? Satisfied when the server publishes it,
// when it's already resident (a synthesized scene.json), or — for a decoder —
// when only its fp16 sibling ships. That last case is why this isn't a plain
// `has()`: an fp16-only bundle is legal, and the scene manifest always names the
// fp32 file, so demanding the literal name would reject a bundle that loads fine.
//
// Both arguments are plain name collections, so this stays pure and testable
// independently of the /ogs row shape.
export function isRequiredFileSatisfied(name, availableNames, residentNames = []) {
  const available = availableNames instanceof Set ? availableNames : new Set(availableNames)
  const resident = residentNames instanceof Set ? residentNames : new Set(residentNames)
  if (resident.has(name) || available.has(name)) return true
  // The sibling rule applies ONLY to geom/appr. They are the two stages with an
  // fp16 asset and a lazy fp32 fallback (multiRegionOptionalFileList emits their
  // fp16 names; decoderResolve fetches the fp32 on demand). The TRUNK has
  // neither — OGSBranch.buildNodes takes its bytes eagerly and throws when they
  // are absent — so accepting a trunk_*_fp16.onnx here would pass validation and
  // then hard-fail at branch build, after the whole bundle had downloaded.
  if (!FP16_CAPABLE_STEMS.includes(splitBundleName(name).stem)) return false
  const sib = fp16NameFor(name)
  return !!(sib && sib !== name && available.has(sib))
}

// Mesh-only bundle: a rig GLB plus clip GLBs and none of the inference assets.
// Any inference marker (scene.json / export_manifest.json / *.onnx) disqualifies.
// Returns { rigUrl, clips: [{ name, url }] } or null.
export function selectMeshOnlyFiles(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null
  let rigUrl = null
  const clips = []
  for (const r of rows) {
    const name = r?.filename ?? r?.name
    const url = r?.fileUrl ?? r?.file_url
    if (!name || !url) continue
    if (name === 'scene.json' || name === 'export_manifest.json' || name.endsWith('.onnx')) return null
    if (name === 'rig.glb') rigUrl = url
    else if (name.endsWith('.glb')) clips.push({ name: name.slice(0, -4), url })
  }
  if (!rigUrl) return null
  return { rigUrl, clips }
}
